import React, { useEffect, useRef, useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { useToast } from "../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationErrors";
import api from "../../service";
import { useCallback } from "react";
import FormButton from "../../components/FormButton";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../utils/formValidations";
import { downloadByUrl } from "../../utils/file";
import { format } from "date-fns";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  SubmitContainer,
  GoBackContainer,
  ActionContainer,
  BadgeContainer,
  LinkButton,
  NotificationContainer,
} from "./styles";
import SelectWithLabel from "../../components/SelectWithLabel";
import ReportYearEventFilter from "../../components/ReportYearEventFilter";
import useReportYearEventFilter from "../../hooks/useReportYearEventFilter";
import InputWithLabel from "../../components/InputWithLabel";
import Badge from "../../components/Badge";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import { useLoading } from "../../hooks/loading";
import Table from "../../components/Table";
import credentialOriginsConstants from "../../constants/credentialOrigins";
import credentialTypesConstants from "../../constants/credentialTypes";
import {
  createCredentialReport,
  getCredentialReportStatus,
} from "../../service/credentials-report.service";

const ManageCredentialReport = () => {
  const { token } = useSelector((state) => state.auth);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);
  const [filename, setFilename] = useState();
  const [data, setData] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState();
  const [selectedRole, setSelectedRole] = useState();
  const [accounts, setAccounts] = useState([]);
  const [filtered, setFiltered] = useState(false);
  const credentialOrigins = credentialOriginsConstants();
  const credentialTypes = credentialTypesConstants();
  const [isExporting, setIsExporting] = useState(false);

  const clearResults = useCallback(() => {
    setShowExport(false);
    setData([]);
    setFiltered(false);
  }, []);

  const onYearChange = useCallback(() => {
    setSelectedEvent(undefined);
    setAccounts([]);
    formRef.current?.setFieldValue("account", "");
    clearResults();
  }, [clearResults]);

  const {
    years,
    events,
    eventFieldDisabled,
    onChangeYear,
    resetYearEventFilter,
  } = useReportYearEventFilter({
    formRef,
    eventValueKey: "idNumber",
    onYearChange,
  });

  const schema = Yup.object().shape({
    year: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "report_filter_year_required" })),
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    role: Yup.string().nullable(),
    account: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    credential_name: Yup.string().nullable(),
  });

  const roles = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "all" }),
        value: null,
      },
      {
        label: intl.formatMessage({ id: "exhibitor" }),
        value: "EXHIBITOR",
      },
      {
        label: intl.formatMessage({ id: "sponsor_without_m2" }),
        value: "SPONSOR_WITHOUT_M2",
      },
      {
        label: intl.formatMessage({ id: "service_provider" }),
        value: "SERVICE_PROVIDER",
      },
    ],
    [intl]
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        await schema.validate(data, { abortEarly: false });

        api
          .patch(
            `/admin/manage-credential`,
            {
              eventId: data.event,
              userId: data.account,
              clientType: data.role,
              credentialName: data.credential_name,
              year: data.year ? Number(data.year) : null,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            setData(result.data.credentialReportResponse);
            setShowExport(true);
          })
          .catch((err) => {
            setData([]);
            setShowExport(false);
          })
          .finally(() => {
            setLoading(false);
            setFiltered(true);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [loading, schema, token]
  );

  useEffect(() => {
    formRef.current.setFieldValue("account");
    if (selectedEvent && selectedRole !== undefined) {
      handleLoading(true);
      api
        .patch(
          `/users/by-event-and-role`,
          {
            eventId: selectedEvent,
            roleType: selectedRole,
            useEmailInTheServiceProviderLabel: false,
            useCancelledOpportunities: false,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((result) =>
          setAccounts([
            { value: 0, label: intl.formatMessage({ id: "all" }) },
            ...result.data.accountDropdownOutputList,
          ])
        )
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    }
  }, [addToast, intl, selectedEvent, selectedRole, token, handleLoading]);

  const handleExport = async () => {
    try {
      setIsExporting(true);

      const formData = formRef.current.getData();

      const body = {
        eventId: formData.event,
        userId: formData.account,
        clientType: selectedRole,
        credentialName: formData.credential_name,
        year: formData.year ? Number(formData.year) : null,
      };
      const response = await createCredentialReport(token, body);

      const { reportId } = response.data;

      startPolling(reportId);
    } catch (error) {
      setIsExporting(false);
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "api.error." + error.response.data.code,
        }),
      });
    }
  };

  const startPolling = async (reportId) => {
    try {
      const response = await getCredentialReportStatus(reportId);

      const { status, downloadUrl } = response.data;

      if (status === "PROCESSED") {
        setIsExporting(false);

        downloadByUrl(downloadUrl);
        return;
      }

      if (status === "ERROR") {
        setIsExporting(false);
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "credential_report_error",
          }),
        });
        return;
      }

      setTimeout(() => {
        startPolling(reportId);
      }, 5000);
    } catch (error) {
      setIsExporting(false);
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "api.error." + error.response.data.code,
        }),
      });
    }
  };

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const clearFields = () => {
    formRef.current.reset();
    formRef.current.setFieldValue("role", "");
    formRef.current.setFieldValue("account", "");
    formRef.current.setFieldValue("credential_name", "");
    setSelectedEvent(undefined);
    setAccounts([]);
    resetYearEventFilter();
    clearResults();
  };

  const onChangeEvent = (event) => {
    setFilename(event?.label?.toUpperCase());
    setSelectedEvent(event?.value);
    clearResults();
    formRef.current.setFieldValue("account", "");
  };

  const PRINT_CHANNEL_LABELS = {
    UNKNOWN: intl.formatMessage({ id: "UNKNOWN" }),
    ADMIN: intl.formatMessage({ id: "ADMIN" }),
    TOTEM: intl.formatMessage({ id: "TOTEM" }),
    CAEX: intl.formatMessage({ id: "CAEX" }),
  };

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "event_edition" }),
        selector: (row) => row.eventName,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "company_name" }),
        selector: (row) => row.accountName,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "commercial_name" }),
        selector: (row) => row.accountAlias,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "printChannels" }),
        selector: (row) => {
          if (!row?.printChannels) {
            return intl.formatMessage({ id: "NOT_PRINTED" });
          }

          return row.printChannels
            .split(",")
            .map((channel) => {
              const key = channel.trim();

              return PRINT_CHANNEL_LABELS[key] ?? key;
            })
            .join(", ");
        },
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "lastPrintedAt" }),
        cell: (row) =>
          row.lastPrintedAt
            ? format(new Date(row.lastPrintedAt), "dd/MM/yyyy - HH:mm")
            : "",
        minWidth: "250px",
      },
      {
        name: intl.formatMessage({ id: "duplicate_printout" }),
        cell: (row) =>
          row.duplicatePrintoutDate
            ? format(new Date(row.duplicatePrintoutDate), "dd/MM/yyyy - HH:mm")
            : "",
        minWidth: "250px",
      },
      {
        name: intl.formatMessage({ id: "id_number_cnpj_passport" }),
        selector: (row) => row.accountCode,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "credential_name" }),
        selector: (row) => row.credentialName,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "160px",
      },
      {
        name: intl.formatMessage({ id: "type" }),
        selector: (row) => row.credentialType,
        cell: (row) =>
          credentialTypes.filter((pt) => pt.value === row.credentialType)[0]
            ?.label,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        maxWidth: "110px",
      },
      {
        name: intl.formatMessage({ id: "origin" }),
        selector: (row) => row.origin,
        cell: (row) =>
          credentialOrigins.filter((pt) => pt.value === row.origin)[0]?.label,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        maxWidth: "110px",
      },
      {
        name: intl.formatMessage({ id: "credential_emission_date" }),
        sortable: true,
        cell: (row) =>
          format(new Date(row.credentialEmissionDate), "dd/MM/yyyy"),
      },
      {
        name: intl.formatMessage({ id: "email" }),
        selector: (row) => row.email,
        sortable: false,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "city" }),
        selector: (row) => row.city,
        sortable: false,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "state" }),
        selector: (row) => row.state,
        sortable: false,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "country" }),
        selector: (row) => row.country,
        sortable: false,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "accredited_document" }),
        selector: (row) => row.accreditedDoc,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "180px",
      },
      {
        name: intl.formatMessage({ id: "accredited_name" }),
        selector: (row) => row.accreditedName,
        allowOverflow: true,
        wrap: true,
        minWidth: "160px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "accredited_job" }),
        selector: (row) => row.accreditedJob,
        sortable: true,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "printed_credential" }),
        cell: (row) =>
          row.printed
            ? intl.formatMessage({ id: "yes" })
            : intl.formatMessage({ id: "no" }),
      },
      {
        name: intl.formatMessage({ id: "bar_code" }),
        selector: (row) => row.barCode,
        sortable: false,
        allowOverflow: true,
        wrap: true,
        minWidth: "115px",
      },
    ],
    [intl]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({ id: "breadcrumb.credential_report" })}
    >
      <Container>
        <FormContainer>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <TitleContainer>
              <Title>
                <FormattedMessage id="export_credential_report" />
              </Title>
              <GoBackContainer>
                <ActionContainer>
                  <BadgeContainer>
                    <LinkButton onClick={() => history.push("/dashboard")}>
                      <Badge
                        color="#002244"
                        text={intl.formatMessage({ id: "goback" })}
                        fontSize="12px"
                      />
                    </LinkButton>
                  </BadgeContainer>
                </ActionContainer>
              </GoBackContainer>
            </TitleContainer>
            <FieldsContainer>
              <Horizontal>
                <ReportYearEventFilter
                  years={years}
                  events={events}
                  onChangeYear={onChangeYear}
                  onChangeEvent={onChangeEvent}
                  eventFieldDisabled={eventFieldDisabled}
                  validationBlur={validationBlur}
                />
                <SelectWithLabel
                  name="role"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "type" })}
                  placeholder={intl.formatMessage({ id: "type" })}
                  labelOrientation="vertical"
                  options={roles}
                  onChange={(role) => setSelectedRole(role?.value)}
                  required
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="account"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "account" })}
                  placeholder={intl.formatMessage({ id: "account" })}
                  labelOrientation="vertical"
                  labelWidth="150px"
                  options={accounts}
                  required
                />
                <InputWithLabel
                  name="credential_name"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "credential_name" })}
                  placeholder={intl.formatMessage({ id: "credential_name" })}
                />
              </Horizontal>
              <Horizontal>
                <FilterActionsContainer>
                  <SubmitContainer>
                    <FormButton
                      color="#C9C9C9"
                      type="button"
                      onClick={clearFields}
                    >
                      <FormattedMessage id="clear_fields" />
                    </FormButton>
                  </SubmitContainer>
                  <SubmitContainer>
                    <FormButton type="submit" loading={loading}>
                      <FormattedMessage id="search" />
                    </FormButton>
                  </SubmitContainer>
                  {showExport ? (
                    <SubmitContainer>
                      <FormButton
                        type="button"
                        onClick={handleExport}
                        loading={isExporting}
                      >
                        <FormattedMessage id="export" />
                      </FormButton>
                    </SubmitContainer>
                  ) : null}
                </FilterActionsContainer>
              </Horizontal>
            </FieldsContainer>
          </Form>
        </FormContainer>
      </Container>
      {filtered && data.length > 0 ? (
        <Table
          columns={columns}
          data={data}
          title={intl.formatMessage({ id: "results" })}
          hasPagination
        />
      ) : (
        <NotificationContainer>
          <FormattedMessage
            id={
              filtered
                ? "report_filter_no_data_for_year_and_event"
                : "need_to_filter_first"
            }
          />
        </NotificationContainer>
      )}
    </AnimatedMainContainer>
  );
};

export default ManageCredentialReport;
